/**
 * F 154 C2 — paste normalisation.
 *
 * WHY THERE IS NO TURNDOWN HERE
 * -----------------------------
 * The dispatch specifies "turndown + gfm". I did not add it, for three
 * reasons that are specific to this codebase rather than taste:
 *
 *  1. `src/core/markdown/allowlist.ts` explicitly REJECTED DOMPurify with the
 *     reasoning "would cost ~20 KB gz and require a real DOM for a check that
 *     a tag scan already does". Turndown needs a real DOM. Adding it would
 *     reverse a decision this repo made on purpose.
 *  2. Turndown is a SECOND html->markdown path competing with the one
 *     markdown parser the doctrine mandates (markdown-it v14, one parser).
 *  3. `@blocknote/core` ALREADY ships `tryParseHTMLToBlocks()` — a maintained
 *     HTML->Block[] converter, already a hard dependency, already in the
 *     bundle. It emits canonical Block[] DIRECTLY, which is the F 145 Job B
 *     canonical form, so it skips the markdown detour entirely.
 *
 * So the converter is INJECTED. This module owns detection, sanitisation and
 * orchestration — all pure, all tested — and the host supplies the converter.
 * The test suite runs the pipeline end to end with a stub converter; FCC wires
 * BlockNote's real one.
 *
 * Detection is BEST-EFFORT BY DESIGN. On any ambiguity we fall back to plain
 * text, which is the only choice that cannot lose structure. Mis-detecting
 * markdown as plain text costs a little fidelity; mis-detecting plain text as
 * Word could mangle it.
 */

export type PasteSource =
  | 'word'
  | 'google-docs'
  | 'web'
  | 'markdown'
  | 'chatbot'
  | 'plain';

export interface PastePayload {
  text?: string | null;
  html?: string | null;
}

/** Converter supplied by the host. Returns canonical FCr blocks. */
export type HtmlToBlocks = (html: string) => unknown[];

export interface NormaliseResult {
  blocks: unknown[];
  source: PasteSource;
  /** What the sanitiser removed. Never silent. */
  stripped: StripReport;
}

export interface StripReport {
  scriptTags: number;
  styleTags: number;
  /** `mso-*`, `class=`, `style=`, `data-*`, `id=` — Office and CMS noise. */
  junkAttributes: number;
  comments: number;
  officeMarkup: number;
}

// ── detection ──────────────────────────────────────────────────────────────

/**
 * Classify a paste. Order matters: Office/Docs markers are checked before the
 * generic "web" branch, because a Word fragment IS HTML and would otherwise be
 * swallowed by it.
 */
export function detectPasteSource(payload: PastePayload): PasteSource {
  const html = payload.html ?? '';
  const text = payload.text ?? '';

  if (looksLikeWord(html, text)) return 'word';
  if (looksLikeGoogleDocs(html)) return 'google-docs';
  if (looksLikeWebHtml(html)) return 'web';
  if (looksLikeMarkdown(text)) return 'markdown';
  return 'plain';
}

function looksLikeWord(html: string, text: string): boolean {
  const h = html.toLowerCase();
  return (
    h.includes('mso-') ||
    h.includes('<o:p>') ||
    h.includes('<!--[if') ||
    h.includes('msonormal') ||
    text.includes('mso-') ||
    // The Word "class=MsoNormal" survives into plain-text pastes surprisingly
    // often, because Word puts styling in the text/html flavour.
    /class\s*=\s*["']?Mso/i.test(html)
  );
}

function looksLikeGoogleDocs(html: string): boolean {
  const h = html.toLowerCase();
  return (
    h.includes('docs-internal-guid') ||
    h.includes('google-sheets-html-origin') ||
    h.includes('<b id="docs-') ||
    h.includes('aria-level=')   // Docs' outline list marker
  );
}

function looksLikeWebHtml(html: string): boolean {
  const h = html.toLowerCase();
  return (
    h.includes('<a ') || h.includes('<img') || h.includes('<table') ||
    h.includes('<ul') || h.includes('<ol') || h.includes('<h1') ||
    /<div\b/.test(h) || h.includes('<p>') || h.includes('<p ')
  );
}

/**
 * Markdown detection. Deliberately conservative — a leading `- ` is a bullet,
 * a leading `- ` inside prose is not a document. Requiring a marker at the
 * START of a line is what keeps ordinary prose out.
 */
function looksLikeMarkdown(text: string): boolean {
  if (!text.trim()) return false;
  const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
  if (lines.length === 0) return false;

  const structural = lines.filter(
    (l) =>
      /^#{1,6}\s/.test(l) ||
      /^[-*+]\s/.test(l) ||
      /^\d+\.\s/.test(l) ||
      /^>\s/.test(l) ||
      /^```/.test(l) ||
      /^\|.*\|$/.test(l) ||
      /^([-*_]\s*){3,}$/.test(l) ||
      /^\[[^\]]+\]\([^)]+\)/.test(l),
  );

  // One structured line in three is a document; one in a chat message is a
  // coincidence. This threshold is the whole heuristic.
  return structural.length >= 1 && structural.length / lines.length >= 0.34;
}

// ── sanitisation ───────────────────────────────────────────────────────────

/**
 * Remove everything we will not carry into Block[].
 *
 * Runs on a STRING and needs no DOM, for the same reason `sanitizeMarkdownOutput`
 * does. Idempotent, and never throws: a malformed paste degrades to whatever
 * survived rather than blowing up the paste handler.
 */
export function sanitizePastedHtml(html: string): { html: string; report: StripReport } {
  const report: StripReport = {
    scriptTags: 0, styleTags: 0, junkAttributes: 0, comments: 0, officeMarkup: 0,
  };
  if (typeof html !== 'string' || !html) {
    return { html: '', report };
  }

  let out = html;

  // 1. dangerous subtrees, content included
  out = out.replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, () => { report.scriptTags++; return ''; });
  out = out.replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, () => { report.styleTags++; return ''; });
  // Self-closing / unclosed variants
  out = out.replace(/<script\b[^>]*\/?>/gi, () => { report.scriptTags++; return ''; });
  out = out.replace(/<style\b[^>]*\/?>/gi, () => { report.styleTags++; return ''; });
  out = out.replace(/<(head|meta|link|title)\b[^>]*>[\s\S]*?<\/\1>/gi, '');
  out = out.replace(/<(meta|link|title)\b[^>]*\/?>/gi, '');

  // 2. comments — Word wraps conditional comments around almost everything
  out = out.replace(/<!--[\s\S]*?-->/g, () => { report.comments++; return ''; });

  // 3. Office namespaced elements and spacers
  out = out.replace(/<\/?(o:[a-z]+|xml|w:[a-z]+|v:[a-z]+)\b[^>]*>/gi, () => { report.officeMarkup++; return ''; });
  out = out.replace(/<!--\[if[^]*?<!\[endif\]-->/gi, () => { report.officeMarkup++; return ''; });

  // 4. attributes. `on*` handlers go first and unconditionally — they are the
  //    XSS vector and must not depend on the allowlist below.
  out = out.replace(/\son[a-z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, () => { report.junkAttributes++; return ''; });
  out = out.replace(
    /\s(mso-[a-z-]+|class|style|data-[\w-]+|id|xmlns(:[\w]+)?|dir|lang|align|valign|bgcolor|width|height|border)\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi,
    () => { report.junkAttributes++; return ''; },
  );

  return { html: out, report };
}

// ── orchestration ──────────────────────────────────────────────────────────

/**
 * The full pipeline. Pure except for the injected converter, so the whole
 * thing is testable without a browser or a BlockNote instance.
 */
export function normalisePaste(
  payload: PastePayload,
  deps: {
    /** Supplied by the host. In FCC this is BlockNote's. */
    htmlToBlocks: HtmlToBlocks;
    /** Supplied by the host. markdown -> canonical blocks. */
    markdownToBlocks: (markdown: string) => unknown[];
  },
): NormaliseResult {
  const source = detectPasteSource(payload);
  const rawHtml = payload.html ?? '';
  const text = payload.text ?? '';

  // Every HTML route is sanitised first, including "web" — that branch is
  // precisely the one carrying attacker-controlled markup.
  if (source === 'word' || source === 'google-docs' || source === 'web') {
    const { html, report } = sanitizePastedHtml(rawHtml);
    if (html.trim()) {
      return { blocks: deps.htmlToBlocks(html), source, stripped: report };
    }
    // Sanitising left nothing usable — fall through to the text flavour
    // rather than committing an empty document.
    return {
      blocks: deps.markdownToBlocks(text || ''),
      source: 'plain',
      stripped: report,
    };
  }

  if (source === 'markdown' || source === 'chatbot') {
    return {
      blocks: deps.markdownToBlocks(text),
      source,
      stripped: {
        scriptTags: 0, styleTags: 0, junkAttributes: 0, comments: 0, officeMarkup: 0,
      },
    };
  }

  // Plain text: paragraph blocks. No markdown interpretation — a stray `#` in
  // a chat message is text, not a heading.
  return {
    blocks: text
      .split(/\n{2,}/)
      .map((p) => p.trim())
      .filter(Boolean)
      .map((p) => ({ type: 'paragraph', content: [{ type: 'text', text: p }] })),
    source: 'plain',
    stripped: {
      scriptTags: 0, styleTags: 0, junkAttributes: 0, comments: 0, officeMarkup: 0,
    },
  };
}
